import React, { ReactNode, useState } from 'react';
import EditProductWizardStepMain from './EditProductWizardStepMain';
import { LoadingIndicator, WizardStepHeader } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import EditProductWizardStepInfo from './EditProductWizardStepInfo';
import { ValidationScope, ValidationSummary } from '@/components/forms/validation';
import EditProductWizardStepImages from './EditProductWizardStepImages';
import {
    BaseProduct,
    TranslatableText,
    ProductSourceCatalog,
    ProfileCatalogProduct,
    ProductFileAttachment,
    ProductFileImage,
    LocalUnsaved,
    OptionalId,
    ProductType,
    ProjectCatalogProduct,
} from '@/types';
import { useAsyncState, useValidationManager } from '@/utils/hooks';
import theme from './EditProductWizard.module.scss';
import EditProductWizardStepPaintInfo from './EditProductWizardStepPaintInfo';
import { Measurements } from '@/utils/unit-of-measurements';
import { getProfileCatalogFilters as getProfileCatalogFilters } from '@/services/profile-catalog-products';
import { getProjectCatalogFilters } from '@/services/project-catalog';
import { Button, ButtonBar, ConfirmButton } from '@ygtechnologies/rubus-ui';

interface Step {
    name: TranslatableText;
    render: () => ReactNode;
    onNextClick?(): void;
}

type UnsavedBaseProduct = Omit<BaseProduct, 'images'> & {
    images: LocalUnsaved<ProductFileImage>[];
};
export type EditProduct = Omit<BaseProduct, 'widthCm' | 'heightCm' | 'depthCm' | 'lengthCm'> & {
    width: number;
    height: number;
    depth: number;
    length: number;
};

interface EditProductWizardProps {
    sourceCatalog: ProductSourceCatalog;
    product: BaseProduct;
    saving: boolean;
    onFinish: () => void;
    onChange: (product: UnsavedBaseProduct) => void;
    onCancel: () => void;
    allowPriceEdit: boolean;
    productMeasurements: Measurements;
    onProductMeasurementsChange(measurements: Measurements): void;
    suppliers: string[];
    confirmDialogBody?: ReactNode;
}

const EditProductWizard: React.FC<EditProductWizardProps> = ({
    product,
    saving,
    onFinish,
    onChange,
    onCancel,
    allowPriceEdit,
    confirmDialogBody,
    productMeasurements,
    onProductMeasurementsChange,
    sourceCatalog,
    suppliers,
}) => {
    const steps: Step[] = [
        {
            name: <Trans>Description</Trans>,
            render: () => (
                <EditProductWizardStepMain product={product} onChange={handleProductChange} suppliers={suppliers} />
            ),
        },
        {
            name: <Trans>Info</Trans>,
            render: () =>
                product.type === ProductType.CustomPaint ? (
                    <EditProductWizardStepPaintInfo
                        product={product}
                        onChange={handleProductChange}
                        allowPriceEdit={allowPriceEdit}
                    />
                ) : (
                    <EditProductWizardStepInfo
                        product={product}
                        onChange={handleProductChange}
                        allowPriceEdit={allowPriceEdit}
                        sourceCatalog={sourceCatalog}
                        onProductMeasurementsChange={onProductMeasurementsChange}
                        productMeasurements={productMeasurements}
                    />
                ),
        },
        {
            name: <Trans>Add images and attachments</Trans>,
            render: () => (
                <EditProductWizardStepImages
                    images={product.images}
                    onImageChange={handleImagesChange}
                    sourceCatalog={sourceCatalog}
                    attachments={product.attachments}
                    onAttachmentsChange={handleAttachmentsChange}
                />
            ),
        },
    ];

    const [currentStepNumber, setCurrentStepNumber] = useState(0);
    const validationManager = useValidationManager();

    const handleProductChange = (product: BaseProduct) => {
        onChange(product);
    };

    const handleImagesChange = (images: LocalUnsaved<ProductFileImage>[]) => {
        onChange({ ...product, images: images.map((x, i) => ({ ...x, sortOrder: i })) });
    };

    const handleAttachmentsChange = (attachments: ProductFileAttachment[]) => {
        onChange({ ...product, attachments });
    };

    const validate = () => {
        const errors = validationManager.validate();

        return errors.length === 0;
    };

    const changeStep = (change: number) => {
        if (change > 0 && !validate()) {
            return;
        }

        const stepNumber = currentStepNumber + change;

        setCurrentStepNumber(stepNumber);
    };

    const handleSaveClick = async () => {
        if (!validate()) return;
        onFinish();
    };

    if (!product) {
        return <LoadingIndicator />;
    }

    const numberOfSteps = steps.length;
    const hasNextStep = currentStepNumber + 1 < numberOfSteps;
    const hasPreviousStep = currentStepNumber > 0;
    const currentStep = steps[currentStepNumber];

    return (
        <ValidationScope validationManager={validationManager}>
            <div className="form-group">
                <WizardStepHeader currentStep={currentStepNumber + 1} totalSteps={steps.length} header={currentStep.name} />

                <div className="form-group">{currentStep.render()}</div>

                <div className="form-group">
                    <ValidationSummary includeChildScopes />
                </div>

                <ButtonBar>
                    <ButtonBar.Right>
                        <Button variant="tertiary" onClick={onCancel} disabled={saving}>
                            <Trans>Cancel</Trans>
                        </Button>

                        {hasPreviousStep && (
                            <Button variant="tertiary" onClick={(e) => changeStep(-1)} disabled={saving}>
                                <Trans>Back</Trans>
                            </Button>
                        )}

                        {hasNextStep && (
                            <Button type="submit" variant="primary" onClick={(e) => changeStep(+1)} disabled={saving}>
                                <Trans>Next</Trans>
                            </Button>
                        )}

                        {!hasNextStep && confirmDialogBody && (
                            <ConfirmButton
                                onConfirm={handleSaveClick}
                                dialogTitle={<Trans>Confirm Product Changes</Trans>}
                                dialogBody={confirmDialogBody}>
                                <Trans>Save</Trans>
                            </ConfirmButton>
                        )}

                        {!hasNextStep && !confirmDialogBody && (
                            <Button type="submit" variant="primary" onClick={handleSaveClick} loading={saving}>
                                <Trans>Save</Trans>
                            </Button>
                        )}
                    </ButtonBar.Right>
                </ButtonBar>
            </div>
        </ValidationScope>
    );
};

type ExternalProps = Omit<EditProductWizardProps, 'sourceCatalog' | 'product' | 'suppliers'>;

export const EditProfileCatalogProductWizard: React.FC<ExternalProps & { catalogProduct: ProfileCatalogProduct }> = ({
    catalogProduct,
    ...rest
}) => {
    const [filterData] = useAsyncState(
        () => getProfileCatalogFilters(catalogProduct.ownerProfileId),
        [catalogProduct.ownerProfileId],
        { suppliers: [] }
    );

    return (
        <EditProductWizard
            {...rest}
            product={catalogProduct}
            sourceCatalog={ProductSourceCatalog.Profile}
            suppliers={filterData.suppliers}
        />
    );
};

export const EditProjectCatalogProductWizard: React.FC<
    ExternalProps & { catalogProduct: OptionalId<ProjectCatalogProduct> }
> = ({ catalogProduct, ...rest }) => {
    const [filterData] = useAsyncState(
        () => getProjectCatalogFilters(catalogProduct.projectId),
        [catalogProduct.ownerProfileId],
        { suppliers: [] }
    );

    return (
        <EditProductWizard
            {...rest}
            product={catalogProduct}
            sourceCatalog={ProductSourceCatalog.Project}
            suppliers={filterData.suppliers}
        />
    );
};
